import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Compass, User, Users, MessageSquare, ArrowRight, CheckCircle,
  X, ChevronRight, Target, BookOpen, TrendingUp, Handshake,
  Bot, Sparkles, Share2, FolderSearch, UsersRound, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const goalOptions = [
  { id: 'leads', label: 'Generate more leads', icon: Target },
  { id: 'growth', label: 'Grow my business', icon: TrendingUp },
  { id: 'learning', label: 'Learn and stay current', icon: BookOpen },
  { id: 'network', label: 'Build my network', icon: Handshake },
  { id: 'referrals', label: 'Get and send referrals', icon: Share2 },
];

const firstActions = [
  { id: 'post', label: 'Post an opportunity', icon: Zap, path: '/referrals' },
  { id: 'group', label: 'Join a group', icon: UsersRound, path: '/community' },
  { id: 'browse', label: 'Browse the directory', icon: FolderSearch, path: '/dashboard/directory' },
  { id: 'referral', label: 'Submit a referral', icon: Share2, path: '/referrals' },
];

const moxeyPrompts = [
  'Find leads in my area',
  'Write a follow-up email',
  'Summarize market trends',
  'Plan my day',
];

const steps = [
  {
    id: 'welcome',
    title: 'Welcome to AREA',
    subtitle: 'Your professional alliance starts here.',
    description: 'Join 20,000+ real estate professionals who collaborate, grow, and succeed together.',
    icon: Compass,
  },
  {
    id: 'goals',
    title: 'What are your goals?',
    subtitle: 'Select what matters most to you.',
    description: 'We\'ll personalize your experience based on what you want to achieve.',
    icon: Target,
  },
  {
    id: 'profile',
    title: 'Complete your profile',
    subtitle: 'Help others find and connect with you.',
    description: 'Members with complete profiles receive 3x more referrals and connection requests.',
    icon: User,
  },
  {
    id: 'community',
    title: 'Meet the community',
    subtitle: 'You\'re not alone in this.',
    description: 'AREA is home to agents, brokers, lenders, title partners, inspectors, and more — all working together.',
    icon: Users,
  },
  {
    id: 'first-action',
    title: 'Take your first step',
    subtitle: 'The best time to start is now.',
    description: 'Pick one action to get started. You can always do more later.',
    icon: Zap,
  },
  {
    id: 'moxey-intro',
    title: 'Meet Moxey AI',
    subtitle: 'Your intelligent assistant.',
    description: 'Moxey can help you draft messages, find leads, analyze markets, and plan your day — all from one place.',
    icon: Bot,
  },
  {
    id: 'value',
    title: 'Your dashboard is ready',
    subtitle: 'Everything you need, in one place.',
    description: 'Opportunities, referrals, activity, and community — your business command center is live.',
    icon: TrendingUp,
  },
  {
    id: 'complete',
    title: 'You\'re all set!',
    subtitle: 'Welcome to the alliance.',
    description: 'Come back daily to check new opportunities, connect with professionals, and grow your business.',
    icon: CheckCircle,
  },
];

export default function OnboardingWizard({ onComplete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);
  const [moxeyDemo, setMoxeyDemo] = useState('');
  const [moxeyResponse, setMoxeyResponse] = useState('');

  const getToken = () => document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];

  useEffect(() => {
    const fetchStatus = async () => {
      const token = getToken();
      if (!token) return;
      try {
        const res = await fetch(`${API}/onboarding/status`, {
          credentials: 'include', headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          if (d.onboarding_completed) setVisible(false);
          else setStep(Math.min(d.onboarding_step || 0, steps.length - 1));
        }
      } catch (e) { /* ignore */ }
    };
    fetchStatus();
  }, []);

  const saveStep = async (s) => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${API}/onboarding/step`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ step: s })
      });
    } catch (e) { /* ignore */ }
  };

  const next = () => {
    if (step < steps.length - 1) {
      const n = step + 1;
      setStep(n);
      saveStep(n);
    } else {
      complete();
    }
  };

  const complete = async () => {
    const token = getToken();
    try {
      if (token) {
        await fetch(`${API}/onboarding/complete`, {
          method: 'POST', credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (e) { /* ignore */ }
    setVisible(false);
    if (onComplete) onComplete();
  };

  const dismiss = () => { saveStep(step); setVisible(false); };

  const toggleGoal = (id) => {
    setSelectedGoals(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const tryMoxeyPrompt = (prompt) => {
    setMoxeyDemo(prompt);
    setMoxeyResponse("I'm in preview mode. Full capabilities are coming soon — I'll be able to help you with that and much more.");
  };

  if (!visible) return null;

  const current = steps[step];
  const Icon = current.icon;
  const total = steps.length;
  const progress = ((step + 1) / total) * 100;

  return (
    <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="onboarding-wizard">
      {/* Progress */}
      <div className="h-1 bg-slate-100 flex-shrink-0">
        <div className="h-full bg-[#3D4F7A] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
      </div>

      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 flex-shrink-0">
        <span className="text-[12px] font-semibold text-[#3D4F7A]">{step + 1} / {total}</span>
        <button onClick={dismiss} className="text-slate-400 hover:text-slate-600 p-1" data-testid="onboarding-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content — scrollable */}
      <div className="flex-1 overflow-y-auto px-5 pb-6">
        <div className="max-w-lg mx-auto pt-4 sm:pt-8">
          {/* Icon */}
          <div className="w-14 h-14 rounded-2xl bg-[#3D4F7A]/10 flex items-center justify-center mb-5">
            <Icon className="w-7 h-7 text-[#3D4F7A]" />
          </div>

          {/* Title */}
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight mb-1">{current.title}</h2>
          <p className="text-[15px] text-[#3D4F7A] font-medium mb-3">{current.subtitle}</p>
          <p className="text-sm text-slate-500 leading-relaxed mb-8">{current.description}</p>

          {/* Step-specific content */}

          {/* Step 1: Goals */}
          {current.id === 'goals' && (
            <div className="space-y-2.5 mb-6">
              {goalOptions.map(g => (
                <button key={g.id} onClick={() => toggleGoal(g.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-[14px] border-2 transition-all text-left
                    ${selectedGoals.includes(g.id)
                      ? 'border-[#3D4F7A] bg-[#3D4F7A]/5'
                      : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`onboarding-goal-${g.id}`}
                >
                  <div className={`w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0
                    ${selectedGoals.includes(g.id) ? 'bg-[#3D4F7A] text-white' : 'bg-slate-100 text-slate-500'}`}>
                    <g.icon className="w-4 h-4" />
                  </div>
                  <span className={`text-[14px] font-medium ${selectedGoals.includes(g.id) ? 'text-[#3D4F7A]' : 'text-slate-700'}`}>
                    {g.label}
                  </span>
                  {selectedGoals.includes(g.id) && <CheckCircle className="ml-auto w-5 h-5 text-[#3D4F7A]" />}
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Profile */}
          {current.id === 'profile' && (
            <div className="rounded-[14px] border border-slate-200 p-5 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#3D4F7A] flex items-center justify-center text-white text-lg font-bold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{user?.name || 'Your Name'}</p>
                  <p className="text-xs text-slate-500">{user?.email || 'your@email.com'}</p>
                </div>
              </div>
              <p className="text-xs text-slate-500">Complete your profile to be discoverable in the member directory.</p>
            </div>
          )}

          {/* Step 3: Community preview */}
          {current.id === 'community' && (
            <div className="space-y-2 mb-6">
              {[
                { name: 'Luxury Market Pros', members: '842 members' },
                { name: 'First-Time Buyer Specialists', members: '1.2K members' },
                { name: 'Commercial RE Network', members: '567 members' },
              ].map((g, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-[12px] bg-slate-50">
                  <div className="w-8 h-8 rounded-lg bg-[#3D4F7A]/10 flex items-center justify-center">
                    <UsersRound className="w-4 h-4 text-[#3D4F7A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-800">{g.name}</p>
                    <p className="text-[11px] text-slate-400">{g.members}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 4: First action */}
          {current.id === 'first-action' && (
            <div className="grid grid-cols-2 gap-3 mb-6">
              {firstActions.map(a => (
                <button key={a.id}
                  onClick={() => setSelectedAction(a.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-[14px] border-2 transition-all
                    ${selectedAction === a.id
                      ? 'border-[#3D4F7A] bg-[#3D4F7A]/5'
                      : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`onboarding-action-${a.id}`}
                >
                  <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center
                    ${selectedAction === a.id ? 'bg-[#3D4F7A] text-white' : 'bg-slate-100 text-slate-500'}`}>
                    <a.icon className="w-5 h-5" />
                  </div>
                  <span className={`text-[12px] font-medium text-center leading-tight
                    ${selectedAction === a.id ? 'text-[#3D4F7A]' : 'text-slate-600'}`}>
                    {a.label}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Step 5: Moxey AI intro */}
          {current.id === 'moxey-intro' && (
            <div className="mb-6">
              <div className="rounded-[14px] bg-[#1E293B] p-4 mb-3">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-indigo-400" />
                  <span className="text-xs font-semibold text-white">Try a prompt</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {moxeyPrompts.map((p, i) => (
                    <button key={i} onClick={() => tryMoxeyPrompt(p)}
                      className="text-[11px] px-3 py-1.5 rounded-full bg-white/10 text-slate-300 hover:bg-white/20 transition-colors"
                      data-testid={`onboarding-moxey-prompt-${i}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                {moxeyDemo && (
                  <div className="space-y-2">
                    <div className="flex justify-end">
                      <div className="bg-indigo-500 text-white text-[12px] px-3 py-2 rounded-2xl rounded-br-lg max-w-[80%]">{moxeyDemo}</div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-white/10 text-slate-200 text-[12px] px-3 py-2 rounded-2xl rounded-bl-lg max-w-[80%]">{moxeyResponse}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 6: Value showcase */}
          {current.id === 'value' && (
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[
                { n: '500+', label: 'Referrals shared monthly', color: '#3B82F6' },
                { n: '1.2K', label: 'Active discussions', color: '#10B981' },
                { n: '300+', label: 'Job listings', color: '#F59E0B' },
                { n: '50+', label: 'Partner discounts', color: '#8B5CF6' },
              ].map((s, i) => (
                <div key={i} className="rounded-[12px] bg-slate-50 p-4 text-center">
                  <p className="text-2xl font-bold" style={{ color: s.color }}>{s.n}</p>
                  <p className="text-[11px] text-slate-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Step 7: Complete */}
          {current.id === 'complete' && (
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom actions — fixed */}
      <div className="px-5 pb-6 pt-3 border-t border-slate-100 flex-shrink-0 bg-white">
        <div className="max-w-lg mx-auto flex gap-3">
          <Button variant="ghost" onClick={dismiss} className="text-slate-500 text-[13px]" data-testid="onboarding-skip">
            {current.id === 'complete' ? '' : 'Skip to Dashboard'}
          </Button>
          <Button
            onClick={() => {
              if (current.id === 'complete') { complete(); return; }
              next();
            }}
            className="flex-1 h-12 bg-[#3D4F7A] hover:bg-[#2A3755] text-white rounded-xl text-[14px] font-semibold"
            disabled={false}
            data-testid="onboarding-action"
          >
            {current.id === 'complete' ? 'Go to Dashboard' :
             current.id === 'welcome' ? "Let's go" :
             current.id === 'first-action' && selectedAction ? 'Do it now' :
             'Continue'}
            {current.id !== 'complete' && <ArrowRight className="ml-2 w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
